import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Checkbox, ClickAwayListener, FormControlLabel, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { AlertMessages, FormActionButtons, FormButtonGroup, FormDialog, FormSelect, FormTextField, Loading } from '../../../components';
import styles from "./styles";
import { fetchAppearanceThemes, resetAppearanceThemes, updateAppearanceThemes } from '../../../actions/appearance';
import { getAppearanceThemesData, getIsLoading, getResetAppearanceThemesData, getUpdateAppearanceThemesErrors, getUpdatedAppearanceThemesData } from '../../../reducers/appearance';
import { getIsPortalAdmin } from '../../../reducers/portalConfig';
import { getI18n } from '../../../utils/intl';
import { useIntl } from 'react-intl';
import FileUploadImage from '../../../components/FileUploadImage';
import ColorFormTextField from '../../../components/ColorFormTextField';
import CookieConsent from '../../../components/CookieConsent';
import EditIcon from '@material-ui/icons/Edit';
import clsx from 'clsx';
import HelpIcon from '@material-ui/icons/Help';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { useHistory } from 'react-router';
import CookiePreviewBanner from '../../../components/CookiePreviewBanner';
import PropTypes from "prop-types";
import { DEFAULT_TYPOGRAPHY_OPTIONS, appearanceThemesPayload, convertToIdsFormat, convertToLabelFormat, cookieConsentMessageBase64, getCookieMessage, validateAndSetErrors } from './utils';

const COOKIE_POLICY_POSITION_TOP = "top"
const COOKIE_POLICY_POSITION_BOTTOM = "bottom"
const COOKIE_POLICY_THEME_LIGHT = "light"
const COOKIE_POLICY_THEME_DARK = "dark"

const CreatePageContent = (props) => {
  const {
    classes,
    appearanceThemesData,
    intl,
    handleUploadImage,
    handleChange,
    handleSiteTitle,
    validationErrorFromState,
    handleRemoveImageFile,
    cookieConsentTextFieldValue,
    handleChangeCookieConsent,
    isOrganizationFlow,
  } = props

  const [editSiteTitle, setEditSiteTitle] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [showCookiePreview, setShowCookiePreview] = useState({
    open: false,
    vertical: 'top',
    horizontal: 'center',
    root: classes.rootDark,
    content: '',
  });

  const cookieMessageFromAppearanceThemesData = appearanceThemesData?.display.cookieConsent.message;

  useEffect(() => {
    if (showCookiePreview.open) {
      const rootThemeClass = (appearanceThemesData.display.cookieConsent.theme === 'light' ? classes.rootLight : classes.rootDark);
      const rootPositionClass = (appearanceThemesData.display.cookieConsent.position === 'top' ? classes.rootTop : classes.rootBottom);
      setShowCookiePreview({
        ...showCookiePreview,
        root: clsx(classes.cookieRoot, rootThemeClass, rootPositionClass),
        content: getCookieMessage(cookieConsentTextFieldValue) ||
          getCookieMessage(cookieMessageFromAppearanceThemesData) ||
          getCookieMessage(cookieConsentMessageBase64),
        vertical: appearanceThemesData.display.cookieConsent.position,
        open: showCookiePreview.open,
      });
    }
  }, [showCookiePreview.open, cookieConsentTextFieldValue,
    cookieMessageFromAppearanceThemesData, cookieConsentMessageBase64]);

  const cookiePolicyPositionOptions = [
    {
      name: intl.getI18nMessage("label.appearance.form.cookie.policy.bottom"),
      uuid: COOKIE_POLICY_POSITION_BOTTOM,
    },
    {
      name: intl.getI18nMessage("label.appearance.form.cookie.policy.top"),
      uuid: COOKIE_POLICY_POSITION_TOP,
    },
  ];

  const cookiePolicyThemeOptions = [
    {
      name: intl.getI18nMessage("label.appearance.form.cookie.policy.dark"),
      uuid: COOKIE_POLICY_THEME_DARK,
    },
    {
      name: intl.getI18nMessage("label.appearance.form.cookie.policy.light"),
      uuid: COOKIE_POLICY_THEME_LIGHT,
    },
  ];

  const formSelectFieldContainer = clsx(classes.flexNonBetween, classes.formSelectField);
  const formTextFieldContainer = clsx(validationErrorFromState && classes.errorTextField,
    classes.formTextFieldContainer);

  const getValidationErrorMessage = (field) => {
    return validationErrorFromState && validationErrorFromState[field] &&
      <Typography className={classes.errorMessage}>
        {validationErrorFromState[field].localizedMessage}
      </Typography>;

  };

  const filteredFormTextFieldsForColor = Object.keys(appearanceThemesData?.color)?.filter(key => key !== 'apiHubPrimary' && key !== 'apiHubSecondary');

  const renderColorFormTextFieldsForColor = () => {
    return filteredFormTextFieldsForColor?.map(key => {
      const colorValue = appearanceThemesData?.color[key];
      const errorKey = `color.${key}`;
      const formattedLabels = convertToLabelFormat(key);
      const formattedIds = convertToIdsFormat(key);
      const additionalTextKeys = ["background", "link", "linkHover", "primaryButtonText", "headerBackground"];
      const showAdditionalText = additionalTextKeys.includes(key) ? intl.getI18nMessage("label.appearance.form.api.hub.text") : "";

      return (
        <ColorFormTextField
          id={`appearance-edit-colors-${formattedIds}`}
          key={key}
          colorFormTextField={true}
          label={intl.getI18nMessage(`label.appearance.form.colors.${formattedLabels}`)}
          handleChange={(value) => handleChange(value, key, "color")}
          value={colorValue}
          fieldContainerClass={formTextFieldContainer}
          error={getValidationErrorMessage(errorKey)}
          helperText={getValidationErrorMessage(errorKey)}
          addAdditionalText={showAdditionalText}
        />
      );
    });
  };

  const renderColorFormTextFieldsForFontSize = () => {
    return Object.keys(appearanceThemesData.fontSize)?.map(key => {
      const fontSizeValue = appearanceThemesData.fontSize[key];
      const errorKey = `fontSize.${key}`;
      const formattedLabels = convertToLabelFormat(key);
      const formattedIds = convertToIdsFormat(key);

      return (
        <ColorFormTextField
          id={`appearance-edit-font-size-${formattedIds}`}
          key={key}
          label={intl.getI18nMessage(`label.appearance.form.font.size.${formattedLabels}`)}
          handleChange={(value) => handleChange(value, key, "fontSize")}
          value={fontSizeValue}
          fieldContainerClass={formTextFieldContainer}
          error={getValidationErrorMessage(errorKey)}
          helperText={getValidationErrorMessage(errorKey)}
        />
      );
    });
  };

  const filteredSelectFieldsForTypography = Object.keys(appearanceThemesData?.typography)?.filter(key => key !== 'apiHubFont' && key !== 'custom');

  const renderFormSelectFieldsForTypography = () => {
    return filteredSelectFieldsForTypography?.map(key => {
      const typographyValue = appearanceThemesData.typography[key];
      const errorKey = `typography.${key}`;
      const formattedLabels = convertToLabelFormat(key);
      const formattedIds = convertToIdsFormat(key);

      return (
        <FormSelect
          id={`appearance-edit-typography-${formattedIds}`}
          key={key}
          name={intl.getI18nMessage(`label.appearance.form.colors.${formattedLabels}`)}
          data={DEFAULT_TYPOGRAPHY_OPTIONS}
          noNoneOption
          handleChange={(e) => handleChange(e.target.value, key, "typography")}
          value={typographyValue}
          labelClass={classes.formSelectLabel}
          fieldContainerClass={formSelectFieldContainer}
          selectFieldClass={classes.selectField}
          error={getValidationErrorMessage(errorKey)}
          helperText={getValidationErrorMessage(errorKey)}
        />
      );
    });
  };

  const lastUpdateTimeZone = intl.getI18nMessage("label.appearance.edit.last.updated.time.zone.text") + new Date(appearanceThemesData?.lastUpdated).toString();

  return (
    <>
      <ClickAwayListener onClickAway={() => setEditSiteTitle(false)}>
        <div>
          {
            editSiteTitle ?
              <FormTextField
                id='appearance-edit-title-field'
                fieldContainerClass={classes.siteTitleField}
                value={appearanceThemesData?.siteTitle}
                textFieldClass={classes.siteTitleTextFieldClass}
                handleChange={(value) => handleSiteTitle(value)}
              /> :
              <div className={classes.flexNonBetween}>
                <Typography id='appearance-edit-title' variant='h1' className={classes.siteTitle}>{appearanceThemesData?.siteTitle}</Typography>
                <Button
                  id='appearance-edit-title-button'
                  data-apim-test='appearance-edit-title-button'
                  color="primary"
                  variant="outlined"
                  onClick={() => setEditSiteTitle(true)}
                  className={classes.editIconButton}
                >
                  <EditIcon />
                </Button>
              </div>
          }
        </div>
      </ClickAwayListener>
      <Typography id='appearance-edit-last-updated-date' >{lastUpdateTimeZone}</Typography>
      <Box>
        <Typography id='appearance-edit-images-title' variant='h2'>{intl.getI18nMessage("label.appearance.edit.images.title")}</Typography>
        <hr />
        <Typography id='appearance-edit-images-description'>{intl.getI18nMessage("label.appearance.edit.images.description")}</Typography>
        <div className={classes.flexNonBetween}>
          <FileUploadImage
            id='appearance-edit-upload-image-file'
            name={intl.getI18nMessage("label.appearance.form.upload.logo.text")}
            handleChange={(file) => handleUploadImage(file, "logo")}
            value={appearanceThemesData.logo}
            variant="image-file"
            onRemoveFile={() => handleRemoveImageFile("logo")}
            uploadImageClass={classes.uploadImageClass}
            helperText={getValidationErrorMessage('logo')}
          />
          <FileUploadImage
            id='appearance-edit-upload-button-file'
            name={intl.getI18nMessage("label.appearance.form.upload.fav.icon.text")}
            handleChange={(file) => handleUploadImage(file, "favicon")}
            value={appearanceThemesData.favicon}
            uploadButtonClass={classes.favIconContainer}
            onRemoveFile={() => handleRemoveImageFile("favicon")}
            helperText={getValidationErrorMessage('favicon')}
          />
        </div>
      </Box>
      <Box>
        <Typography id="appearance-edit-api-hub-title" variant='h2'>{intl.getI18nMessage("label.appearance.edit.api.hub.settings.title")}</Typography>
        <hr />
        {!isOrganizationFlow && <>
        <Typography id="appearance-edit-api-hub-description">{intl.getI18nMessage("label.appearance.edit.api.hub.settings.description")}</Typography>
          <div className={classes.flexNonBetween}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="appearance-edit-apihub-copyright"
                      data-apim-test="appearance-edit-apihub-copyright"
                      onChange={(event) => handleChange(event.target.checked, "apihubCopyright", "display")}
                      checked={appearanceThemesData.display.apihubCopyright}
                    />
                  }
                  label={intl.getI18nMessage("label.appearance.form.display.apihub.copyright")}
                />
                <Tooltip title={intl.getI18nMessage("label.appearance.form.display.apihub.copyright.tooltip")} arrow placement="right">
                  <HelpIcon className={classes.displayToolTip} />
                </Tooltip>
            </div>
          <Typography id="appearance-edit-api-hub-title" variant='h3'>{intl.getI18nMessage("label.appearance.edit.api.hub.color.settings.title")}</Typography>
        </>}
        <Typography id="appearance-edit-api-hub-description">{intl.getI18nMessage("label.appearance.edit.api.hub.color.settings.description")}</Typography>
        <div>
          <ColorFormTextField
            id='appearance-edit-api-hub-primary-field'
            colorFormTextField={true}
            label={intl.getI18nMessage("label.appearance.form.settings.api.hub.primary")}
            handleChange={(value) => handleChange(value, "apiHubPrimary", "color")}
            value={appearanceThemesData.color.apiHubPrimary}
            fieldContainerClass={formTextFieldContainer}
            error={getValidationErrorMessage('color.apiHubPrimary')}
            helperText={getValidationErrorMessage('color.apiHubPrimary')}
          />
          <ColorFormTextField
            id='appearance-edit-api-hub-secondary-field'
            colorFormTextField={true}
            label={intl.getI18nMessage("label.appearance.form.settings.api.hub.secondary")}
            handleChange={(value) => handleChange(value, "apiHubSecondary", "color")}
            value={appearanceThemesData.color.apiHubSecondary}
            fieldContainerClass={formTextFieldContainer}
            error={getValidationErrorMessage('color.apiHubSecondary')}
            helperText={getValidationErrorMessage('color.apiHubSecondary')}
          />
          <FormSelect
            id='appearance-edit-api-hub-typography-field'
            name={intl.getI18nMessage("label.appearance.form.settings.api.hub.typography")}
            data={DEFAULT_TYPOGRAPHY_OPTIONS}
            handleChange={(e) => handleChange(e.target.value, "apiHubFont", "typography")}
            value={appearanceThemesData.typography.apiHubFont}
            labelClass={classes.formSelectLabel}
            fieldContainerClass={`${classes.flexNonBetween} ${classes.selectFieldApiHubPrimary}`}
            selectFieldClass={classes.selectField}
            error={getValidationErrorMessage('typography.apiHubFont')}
            helperText={getValidationErrorMessage('typography.apiHubFont')}
          />
        </div>
      </Box>
      <Box>
        <div className={classes.flexNonBetween}>
          <Typography id='appearance-edit-colors-title' variant='h2'>{intl.getI18nMessage("label.appearance.form.colors.title")}</Typography>
          <Tooltip title={intl.getI18nMessage("label.appearance.form.colors.tooltip.description")} arrow placement="right">
            <HelpIcon className={classes.toolTipIcon} />
          </Tooltip>
        </div>
        <hr />
        <Typography id='appearance-edit-colors-description'>{intl.getI18nMessage("label.appearance.form.colors.helpertext")}</Typography>
        {
          renderColorFormTextFieldsForColor()
        }
      </Box>
      <Box>
        <div className={classes.flexNonBetween}>
          <Typography id='appearance-edit-font-sizing-text' variant='h2'>{intl.getI18nMessage("label.appearance.form.font.sizing.title")}</Typography>
          <Tooltip title={intl.getI18nMessage("label.appearance.form.font.sizing.tooltip.description")} arrow placement="right">
            <HelpIcon className={classes.toolTipIcon} />
          </Tooltip>
        </div>
        <hr />
        {
          renderColorFormTextFieldsForFontSize()
        }
      </Box>
      <Box>
        <Typography id='appearance-edit-typography-title' variant='h2'>{intl.getI18nMessage("label.appearance.form.typography.title")}</Typography>
        <hr />
        {
          renderFormSelectFieldsForTypography()
        }
      </Box>
      <Box>
        <div className={classes.flexNonBetween}>
          <Typography id='appearance-edit-display-title' variant='h2'>{intl.getI18nMessage("label.appearance.form.display.title")}</Typography>
          <Tooltip title={intl.getI18nMessage("label.appearance.form.display.tooltip")} arrow placement="right">
            <HelpIcon className={classes.toolTipIcon} />
          </Tooltip>
        </div>
        <hr />
        {
          !isOrganizationFlow &&
          <>
            <div className={classes.flexNonBetween}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="appearance-edit-version-number"
                    data-apim-test="appearance-edit-version-number"
                    onChange={(event) => handleChange(event.target.checked, "versionNumber", "display")}
                    checked={appearanceThemesData.display.versionNumber}
                  />
                }
                label={intl.getI18nMessage("label.appearance.form.display.version.number")}
              />
              <Tooltip title={intl.getI18nMessage("label.appearance.form.display.version.number.tooltip")} arrow placement="right">
                <HelpIcon className={classes.displayToolTip} />
              </Tooltip>
            </div>
            <div className={classes.flexNonBetween}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="appearance-edit-copyright"
                    data-apim-test="appearance-edit-copyright"
                    onChange={(event) => handleChange(event.target.checked, "copyright", "display")}
                    checked={appearanceThemesData.display.copyright}
                  />
                }
                label={intl.getI18nMessage("label.appearance.form.display.copyright")}
              />
              <Tooltip title={intl.getI18nMessage("label.appearance.form.display.copyright.tooltip")} arrow placement="right">
                <HelpIcon className={classes.displayToolTip} />
              </Tooltip>
            </div>
          </>
        }
        <div className={classes.flexNonBetween}>
          <FormControlLabel
            control={
              <Checkbox
                id="appearance-edit-cookie-consent"
                data-apim-test="appearance-edit-cookie-consent"
                onChange={(event) => handleChange(event.target.checked, "cookieConsent.enabled", "display")}
                checked={appearanceThemesData.display.cookieConsent.enabled}
              />
            }
            label={intl.getI18nMessage("label.appearance.form.display.cookie.consent")}
          />
          <Tooltip title={intl.getI18nMessage("label.appearance.form.display.cookie.consent.tooltip")} arrow placement="right">
            <HelpIcon className={classes.displayToolTip} />
          </Tooltip>
        </div>
        <Button
          id='appearance-edit-preview-button'
          data-apim-test='appearance-edit-preview-button'
          variant='contained'
          color='secondary'
          disabled={!appearanceThemesData.display.cookieConsent.enabled}
          className={classes.previewButton}
          onClick={() => setShowCookiePreview({
            ...showCookiePreview,
            open: true,
          })}
        >
          {intl.getI18nMessage("label.appearance.form.display.preview.text")}
        </Button>
        <Button
          id='appearance-edit-edit-button'
          data-apim-test='appearance-edit-edit-button'
          className={classes.editButton}
          disabled={!appearanceThemesData.display.cookieConsent.enabled}
          onClick={() => setIsOpenEdit(value => !value)}
          variant='text'
          color='secondary'
          endIcon={
            isOpenEdit ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
          }
        >
          {intl.getI18nMessage("label.edit")}
        </Button>
      </Box>

      {
        isOpenEdit && appearanceThemesData?.display.cookieConsent.enabled &&
        <Box className={classes.editorContainer}>
          <div className={classes.flexNonBetween}>
            <Typography id='appearance-edit-cookie-consent-title' variant='h4'>{intl.getI18nMessage("label.appearance.form.display.cookie.consent.message.text")}</Typography>
            <Tooltip title={intl.getI18nMessage("label.appearance.form.display.cookie.consent.message.tooltip")} arrow placement="right">
              <HelpIcon className={classes.toolTipIcon} />
            </Tooltip>
          </div>
          <CookieConsent
            editorContainerClass={classes.editorContainerClass}
            defaultCookieMessage={intl.getI18nMessage("label.appearance.form.editor.cookieConsent.defaultMessage")}
            cookieMessageFromApi={cookieMessageFromAppearanceThemesData ?
              cookieMessageFromAppearanceThemesData : cookieConsentMessageBase64}
            value={cookieConsentTextFieldValue}
            onChange={handleChangeCookieConsent}
            helpertext={intl.getI18nMessage("label.appearance.form.editor.helpertext")}
          />
          <div className={classes.FormButtonGroupContainer}>
            <Tooltip title={intl.getI18nMessage("label.appearance.form.cookie.policy.position.tooltip")} arrow placement="right">
              <HelpIcon className={classes.buttonGroupToolTip} />
            </Tooltip>
            <FormButtonGroup
              id='appearance-edit-position'
              name={intl.getI18nMessage("label.appearance.form.cookie.policy.position.text")}
              data={cookiePolicyPositionOptions}
              handleChange={(value) => handleChange(value, "cookieConsent.position", "display")}
              value={appearanceThemesData.display.cookieConsent.position}
            />
          </div>
          <div className={classes.FormButtonGroupContainer}>
            <Tooltip title={intl.getI18nMessage("label.appearance.form.cookie.policy.theme.tooltip")} arrow placement="right">
              <HelpIcon className={classes.buttonGroupToolTip} />
            </Tooltip>
            <FormButtonGroup
              id='appearance-edit-theme'
              name={intl.getI18nMessage("label.appearance.form.cookie.policy.theme.text")}
              data={cookiePolicyThemeOptions}
              handleChange={(value) => handleChange(value, "cookieConsent.theme", "display")}
              value={appearanceThemesData.display.cookieConsent.theme}
            />
          </div>
        </Box>
      }
      <CookiePreviewBanner
        handleClose={() => setShowCookiePreview({
          ...showCookiePreview,
          open: false,
        })}
        open={showCookiePreview.open}
        content={showCookiePreview.content}
        root={showCookiePreview.root}
        horizontal={showCookiePreview.horizontal}
        vertical={showCookiePreview.vertical}
      />
    </>

  )
}

CreatePageContent.propTypes = {
  classes: PropTypes.object,
  appearanceThemesData: PropTypes.object,
  intl: PropTypes.object,
  handleUploadImage: PropTypes.func,
  handleChange: PropTypes.func,
  handleSiteTitle: PropTypes.func,
  cookieConsentTextField: PropTypes.object,
  validationErrors: PropTypes.object,
  handleRemoveImageFile: PropTypes.func,
  cookieConsentTextFieldValue: PropTypes.string,
  handleChangeCookieConsent: PropTypes.func,
  isOrganizationFlow: PropTypes.bool,
  validationErrorFromState: PropTypes.object,
}

function EditAppearancePage(props) {
  const [applyChangesModalOpen, setApplyChangesModalOpen] = useState(false);
  const [validationErrorFromState, setValidationErrorFromState] = useState(null)
  const [cookieConsentTextFieldValue, setCookieConsentTextFieldValue] = useState("");
  const [showAlert, setShowAlert] = useState({
    show: false,
    variant: "",
    message: "",
  })
  const [payload, setPayload] = useState(appearanceThemesPayload);

  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = getI18n(useIntl());
  const history = useHistory();

  const isOrganizationFlow = history.location.pathname !== "/appearance/global";
  const orgUuid = props?.match.params.orgUuid;

  const appearanceThemesData = useSelector((state) => getAppearanceThemesData(state));
  const isLoading = useSelector((state) => getIsLoading(state));
  const updatedAppearanceData = useSelector((state) => getUpdatedAppearanceThemesData(state));
  const resetAppearanceData = useSelector((state) => getResetAppearanceThemesData(state));
  const validationErrors = useSelector((state) => getUpdateAppearanceThemesErrors(state));

  useEffect(() => {
    if (validationErrors) {
      setValidationErrorFromState(validationErrors)
    }
  }, [validationErrors])

  useEffect(() => {
    if (isOrganizationFlow) {
      dispatch(fetchAppearanceThemes(orgUuid))
    } else {
      dispatch(fetchAppearanceThemes())
    }
  }, [isOrganizationFlow, orgUuid]);

  useEffect(() => {
    if (appearanceThemesData) {
      setPayload({
        ...payload,
        ...appearanceThemesData,
        image: { ...payload.image, ...appearanceThemesData.image },
        color: { ...payload.color, ...appearanceThemesData.color },
        typography: { ...payload.typography, ...appearanceThemesData.typography },
        fontSize: { ...payload.fontSize, ...appearanceThemesData.fontSize },
        display: {
          ...payload.display,
          copyright: appearanceThemesData.display.copyright,
          versionNumber: appearanceThemesData.display.versionNumber,
          apihubCopyright: appearanceThemesData.display.apihubCopyright,
          cookieConsent: {
            ...payload.display.cookieConsent,
            ...appearanceThemesData.display.cookieConsent,
          },
        },
      });
    }
  }, [appearanceThemesData]);

  const handleChangeCookieConsent = (newValue) => {
    setCookieConsentTextFieldValue(newValue);
  }

  const handleSiteTitle = (value) => {
    setPayload({
      ...payload,
      siteTitle: value,
    })
  }

  const handleChange = (value, fieldName, fieldType) => {
    validateAndSetErrors(fieldType, fieldName, value, intl, setValidationErrorFromState);

    const fieldNames = fieldName.split('.');
    if (fieldNames.length > 1) {
      setPayload(prevState => ({
        ...prevState,
        [fieldType]: {
          ...prevState[fieldType],
          [fieldNames[0]]: {
            ...prevState[fieldType][fieldNames[0]],
            [fieldNames[1]]: value,
          },
        },
      }));
    } else {
      setPayload(prevState => ({
        ...prevState,
        [fieldType]: {
          ...prevState[fieldType],
          [fieldName]: value,
        },
      }));
    }
  };

  const handleUploadImage = (file, type) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result;
        if (type === "logo") {
          setPayload({
            ...payload,
            logo: base64String,
          })
        } else if (type === "favicon") {
          setPayload({
            ...payload,
            favicon: base64String,
          });
        }
      };

      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImageFile = (file) => {
    if (file === "logo") {
      setPayload({
        ...payload,
        logo: appearanceThemesData && appearanceThemesData.logo,
      })
    } else if (file === "favicon") {
      setPayload({
        ...payload,
        favicon: appearanceThemesData && appearanceThemesData.favicon,
      })
    }
  }

  const handleUpdateAppearanceThemes = () => {
    payload.display.cookieConsent.message = cookieConsentTextFieldValue ||
      appearanceThemesData.display.cookieConsent.message ||
      cookieConsentMessageBase64;
    delete payload?.typography?.custom;
    if (isOrganizationFlow) {
      delete payload.display.versionNumber;
      delete payload.display.copyright;
      delete payload.display.apihubCopyright;
      dispatch(updateAppearanceThemes(payload, orgUuid));
    } else {
      dispatch(updateAppearanceThemes(payload));
    }
    setApplyChangesModalOpen(false);
  }

  const handleResetAppearanceThemes = () => {
    if (isOrganizationFlow) {
      dispatch(resetAppearanceThemes(orgUuid));
    } else {
      dispatch(resetAppearanceThemes());
    }
  }


  useEffect(() => {
    if (updatedAppearanceData || resetAppearanceData) {
      setShowAlert({
        show: true,
        variant: "success",
        message: intl.getI18nMessage("label.appearance.form.update.success.message"),
      })
      isOrganizationFlow ? window.location.href = `/appearance/organization/${orgUuid}` : window.location.href = `/appearance/global`;
    } else if (validationErrors) {
      setShowAlert({
        show: true,
        variant: "error",
        message: intl.getI18nMessage("label.appearance.form.updae.error.message"),
      })
    }
  }, [updatedAppearanceData, resetAppearanceData, validationErrors])

  const isPortalAdmin = useSelector((state) => getIsPortalAdmin(state));

  useEffect(() => {
    if(!isPortalAdmin) {
      history.push('/404')
    }
  }, [isPortalAdmin]);

  return (
    <div className={classes.root}>
      {
        isLoading &&
        <Loading pageLoader />
      }
      <CreatePageContent
        classes={classes}
        appearanceThemesData={payload}
        intl={intl}
        handleChange={handleChange}
        handleSiteTitle={handleSiteTitle}
        handleChangeCookieConsent={handleChangeCookieConsent}
        cookieConsentTextFieldValue={cookieConsentTextFieldValue}
        handleUploadImage={handleUploadImage}
        validationErrorFromState={validationErrorFromState}
        handleRemoveImageFile={handleRemoveImageFile}
        isOrganizationFlow={isOrganizationFlow}
      />
      <FormActionButtons
        cancelText={intl.getI18nMessage("label.appearance.form.restore.defaults.button.text")}
        nextText={intl.getI18nMessage("label.appearance.form.apply.changes.button.text")}
        isContainerFooter={true}
        onNextClick={() => setApplyChangesModalOpen(true)}
        onCancelClick={handleResetAppearanceThemes}
      />
      <FormDialog
        isOpen={applyChangesModalOpen}
        title={intl.getI18nMessage("label.appearance.form.confirm.changes.dialog.title")}
        cancelText={intl.getI18nMessage("label.cancel.button")}
        submitText={intl.getI18nMessage("label.save.button")}
        onCancel={() => setApplyChangesModalOpen(false)}
        onSubmit={handleUpdateAppearanceThemes}
      >
        {intl.getI18nMessage("label.appearance.form.confirm.changes.dialog.description")}
      </FormDialog>
      {
        showAlert.show &&
        <AlertMessages
          message={showAlert.message}
          onClose={() => setShowAlert({
            ...showAlert,
            show: false,
          })}
          variant={showAlert.variant}
          shouldAutoCloseSuccessMessages={showAlert.variant === 'success'}
        />
      }
    </div>
  )
}

EditAppearancePage.propTypes = {
  match: PropTypes.object,
}

export default EditAppearancePage;
